import { useEffect, useState, useCallback, memo } from "react";
import { Download, Loader2, Pencil, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { useDownload } from "@/hooks/use-download";
import { capture } from "@/lib/analytics";
import type { Animation, PendingJob, PendingJobsResponse, ShowcaseResponse } from "@/lib/types";
import { DownloadModal } from "@/components/DownloadModal";
import type { Session } from "@/hooks/use-session";

interface ShowcaseCardProps {
  animation?: Animation;
  svgContent: string | null;
  onEdit: (animation: Animation, svgContent: string) => void;
  onDownloadReady: (svgContent: string, downloadUrl: string) => void;
  isPending?: boolean;
  pendingPrompt?: string;
}

const ShowcaseCard = memo(function ShowcaseCard({ 
  animation, svgContent, onEdit, onDownloadReady, isPending, pendingPrompt 
}: ShowcaseCardProps) {
  const { downloading, handleDownload, downloadUrl, purchased } = useDownload(animation?.id ?? "");
  const [downloadHandled, setDownloadHandled] = useState(false);

  useEffect(() => {
    if (!downloadUrl || downloadHandled) return;
    if (!svgContent) return;
    onDownloadReady(svgContent, downloadUrl);
    setDownloadHandled(true);
  }, [downloadUrl, downloadHandled, svgContent, onDownloadReady]);

  const timeAgo = (ts: number) => {
    const diff = Date.now() / 1000 - ts;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}d ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-[1.5rem] bg-card border border-border/10 transition-all duration-300 hover:shadow-2xl hover:shadow-primary/5 hover:-translate-y-1">
      <div className="aspect-square bg-muted/30 flex items-center justify-center p-0 relative overflow-hidden">
        {svgContent ? (
          <div
            className="w-full h-full flex items-center justify-center transition-transform duration-500 group-hover:scale-105"
            dangerouslySetInnerHTML={{ __html: svgContent }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-muted-foreground/50">
              <Loader2 className="w-6 h-6 animate-spin" />
              {isPending && <span className="text-[10px] font-bold uppercase tracking-widest">Pending</span>}
            </div>
          </div>
        )}

        {/* Private Badge */}
        {!isPending && animation && !animation.is_public && (
          <div className="absolute top-4 left-4 z-10">
            <Badge variant="secondary" className="bg-background/80 text-foreground border-border/10 gap-1.5 text-[10px] font-bold uppercase tracking-wider h-6 px-2.5 backdrop-blur-md">
              <EyeOff className="w-3 h-3" />
              Private
            </Badge>
          </div>
        )}

        {/* Hover overlay */}
        {!isPending && animation && svgContent && (
          <div className="absolute inset-0 bg-background/60 opacity-0 group-hover:opacity-100 backdrop-blur-[2px] transition-all duration-300 flex items-center justify-center gap-3">
            <button
              disabled={!svgContent}
              onClick={() => {
                if (!svgContent) return;
                capture("gallery_edit_clicked", { animation_id: animation.id, prompt_length: animation.prompt.length });
                onEdit(animation, svgContent);
              }}
              className="flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-xs font-bold text-background hover:opacity-90 active:scale-95 transition-all"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
            {downloadUrl ? (
              <a
                href={downloadUrl}
                className="flex items-center gap-2 rounded-full border border-border/20 bg-background px-5 py-2.5 text-xs font-bold text-foreground hover:bg-muted active:scale-95 transition-all shadow-xl"
              >
                <Download className="w-3.5 h-3.5" />
                Download
              </a>
            ) : (
              <button 
                onClick={handleDownload} 
                disabled={downloading} 
                className="flex items-center gap-2 rounded-full border border-border/20 bg-background px-5 py-2.5 text-xs font-bold text-foreground hover:bg-muted active:scale-95 transition-all shadow-xl"
              >
                {downloading ? <Loader2 className="animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                {purchased ? "Download" : "$5"}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="p-5 flex flex-col justify-between flex-1 bg-card">
        <p className="text-sm font-medium text-foreground/80 line-clamp-2 leading-relaxed mb-4">
          {isPending ? (pendingPrompt ?? "Generating...") : animation?.prompt}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
            {isPending ? "Pending" : (animation ? timeAgo(animation.created_at) : "")}
          </span>
          {!isPending && animation && animation.download_count > 0 && (
            <span className="flex items-center gap-1.5 text-[10px] font-bold text-muted-foreground/60">
              <Download className="w-3 h-3" />
              {animation.download_count}
            </span>
          )}
        </div>
      </div>
    </div>
  );
});

interface ShowcaseGridProps {
  onEdit: (animation: Animation, svgContent: string) => void;
  session: Session | null | undefined;
}

export function ShowcaseGrid({ onEdit, session }: ShowcaseGridProps) {
  const [animations, setAnimations] = useState<Animation[]>([]);
  const [svgCache, setSvgCache] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<string | undefined>();
  const [loadingMore, setLoadingMore] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadModalSvg, setDownloadModalSvg] = useState<string | null>(null);
  const [downloadModalUrl, setDownloadModalUrl] = useState<string | null>(null);
  const [pendingJobs, setPendingJobs] = useState<PendingJob[]>([]);
  const [prevPendingCount, setPrevPendingCount] = useState(0);

  const fetchSvg = useCallback(async (animation: Animation) => {
    if (!animation.svg_url) return;
    try {
      const res = await fetch(animation.svg_url);
      if (!res.ok) return;
      const svg = await res.text();
      setSvgCache((prev) => ({ ...prev, [animation.id]: svg }));
    } catch {
      // silently fail
    }
  }, []);

  const loadAnimations = useCallback(async (cur?: string) => {
    try {
      const url = cur ? `/api/showcase?cursor=${cur}` : "/api/showcase";
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to load showcase");
      const data = (await res.json()) as ShowcaseResponse;
      setAnimations((prev) => (cur ? [...prev, ...data.animations] : data.animations));
      setCursor(data.cursor);
      data.animations.forEach((a) => fetchSvg(a));
    } catch {
      toast({ variant: "destructive", title: "Could not load gallery" });
    }
  }, [fetchSvg]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadAnimations();
      setLoading(false);
    };
    void init();
  }, [loadAnimations, session?.user?.id]); // Reload if user ID changes

  useEffect(() => {
    let active = true;

    const loadPendingJobs = async () => {
      try {
        const res = await fetch("/api/jobs");
        if (!res.ok) return;
        const data = (await res.json()) as PendingJobsResponse;
        if (!active) return;
        
        const newJobs = data.jobs ?? [];
        
        // If a job finished (count decreased), refresh the animations list
        if (newJobs.length < prevPendingCount) {
          void loadAnimations();
        }
        
        setPendingJobs(newJobs);
        setPrevPendingCount(newJobs.length);
      } catch {
        // ignore
      }
    };

    void loadPendingJobs();
    const interval = setInterval(loadPendingJobs, 4000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [loadAnimations, prevPendingCount]);

  const handleLoadMore = async () => {
    if (!cursor) return;
    capture("gallery_load_more", { current_count: animations.length });
    setLoadingMore(true);
    await loadAnimations(cursor);
    setLoadingMore(false);
  };

  const onDownloadReady = useCallback((svgContent: string, url: string) => {
    setDownloadModalSvg(svgContent);
    setDownloadModalUrl(url);
    setDownloadModalOpen(true);
  }, []);

  return (
    <section id="showcase" className="py-20 space-y-6">
      {downloadModalOpen && downloadModalSvg && downloadModalUrl && (
        <DownloadModal
          open={downloadModalOpen}
          onClose={() => setDownloadModalOpen(false)}
          svgContent={downloadModalSvg}
          downloadUrl={downloadModalUrl}
        />
      )}
      
      {loading && animations.length === 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={`skeleton-${i}`} className="aspect-square bg-card border border-border/50 rounded-xl" />
          ))}
        </div>
      ) : animations.length === 0 ? (
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">No animations yet. Be the first to generate one!</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-end">
            <Button variant="ghost" size="sm" onClick={() => loadAnimations()} className="gap-2 text-muted-foreground">
              Refresh
            </Button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {pendingJobs.map((job) => (
              <ShowcaseCard
                key={`pending-${job.id}`}
                svgContent={null}
                onEdit={onEdit}
                onDownloadReady={() => {}}
                isPending
                pendingPrompt={job.prompt}
              />
            ))}
            {animations.map((animation) => (
              <ShowcaseCard
                key={animation.id}
                animation={animation}
                svgContent={svgCache[animation.id] ?? null}
                onEdit={onEdit}
                onDownloadReady={onDownloadReady}
              />
            ))}
          </div>

          {cursor && (
            <div className="text-center pt-4">
              <Button variant="outline" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? <><Loader2 className="w-4 h-4" />Loading…</> : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
